/**
 * Thin fetch wrapper for the API. The SPA and the API share an origin in every
 * environment (Vite proxies /api in development, one Cloud Run service serves both in
 * production), so requests are same-origin and the session cookie rides along on its own.
 */

export class ApiError extends Error {
  // Declared as fields rather than constructor parameter properties, which
  // `erasableSyntaxOnly` in tsconfig disallows.
  readonly status: number
  readonly problem?: ProblemDetails

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** The caller is not signed in, or the session expired. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /** Signed in, but not permitted. Usually the wrong unit. */
  get isForbidden() {
    return this.status === 403
  }
}

/** RFC 9457 problem details, which is what the API returns for handled failures. */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string[]>
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options

  const response = await fetch(path, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw new ApiError(response.status, await describeFailure(response), await readProblem(response))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

/**
 * Reads the problem details body if there is one. Cloned first, because the body can
 * only be consumed once and the message builder needs it too.
 */
async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  if (!response.headers.get('content-type')?.includes('json')) {
    return undefined
  }

  try {
    return (await response.clone().json()) as ProblemDetails
  } catch {
    return undefined
  }
}

async function describeFailure(response: Response): Promise<string> {
  const problem = await readProblem(response)
  return problem?.detail ?? problem?.title ?? `${response.status} ${response.statusText}`
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
